<script lang="ts" setup>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type Lenis from 'lenis'

/**
 * Registered here rather than in a global plugin. A plugin runs on every page
 * and cannot be scoped to a layout, so the old `plugins/gsap.client.ts` pulled
 * GSAP and ScrollTrigger into the main bundle for the storefront, the booking
 * pages and the account area too — none of which animate. Every page that uses
 * GSAP renders under this layout, and the layout's setup runs before any of its
 * sections mount, so the plugin is in place before the first ScrollTrigger.
 * `registerPlugin` is idempotent, so the three sections that also register it
 * themselves are unaffected.
 */
if (import.meta.client) {
  gsap.registerPlugin(ScrollTrigger)
}

const nuxtApp = useNuxtApp()
const { $lenis } = nuxtApp as unknown as { $lenis?: Lenis }

const appConfig = useAppConfig();

appConfig.ui.primary = "dodger-blue";

const { t } = useI18n()

const whatsappHref = WHATSAPP_HREF

// Final resize + refresh after every section has mounted. Sections that mount
// after a pinned one (Features, HowItWorks) can otherwise compute their
// scrollTrigger start/end positions against a layout that doesn't yet include
// every pin spacer, which causes their entrance animations to fire at the
// wrong scroll position — or to fire prematurely with `once: true` and never
// re-fire when the section is actually scrolled into view. As the layout is the
// parent of the page slot, its onMounted runs after every section has mounted.
//
// This layout instance persists across client-side navigation between landing
// pages (they all use `layout: 'landing'`), so onMounted alone only ever fires
// once. LandingCtaFooter is rendered here in the layout, not inside <slot />,
// so it too only mounts once — its `once: true` ScrollTriggers stay measured
// against whichever page happened to load first. Navigating to a page with a
// different content height leaves those trigger positions stale, sometimes
// past the new page's max scroll, so the footer's animations never fire.
// `page:finish` re-runs this after every navigation, once the new page has
// mounted, to keep trigger positions current.
function refreshScrollTrigger() {
  nextTick(() => {
    $lenis?.resize()
    ScrollTrigger.refresh()
  })
}

onMounted(refreshScrollTrigger)
const unhookPageFinish = nuxtApp.hook('page:finish', refreshScrollTrigger)
onUnmounted(unhookPageFinish)
</script>

<template>
  <div class="overflow-x-hidden text-gray-900 bfm-page">
    <LandingNav />
    <slot />
    <LandingCtaFooter />

    <a
      :href="whatsappHref"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="t('page.whatsapp_float_aria')"
      :title="t('page.whatsapp_float_aria')"
      class="fixed bottom-6 ltr:right-6 rtl:left-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2"
    >
      <UIcon name="i-approved-whatsapp" class="h-7 w-7" />
    </a>
  </div>
</template>

<style>
@keyframes navSlideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }

  to {
    transform: none;
    opacity: 1;
  }
}

.bfm-page .site-nav {
  animation: navSlideDown 0.4s ease;
}

.bfm-page .nav-hamburger.active span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.bfm-page .nav-hamburger.active span:nth-child(2) {
  opacity: 0;
}

.bfm-page .nav-hamburger.active span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.gradient-title {
  background: linear-gradient(135deg, #2E83FB 0%, #CB2097 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
</style>
